// START IMPORTS
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Stack, Box, useTheme } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
import { DebugMenu, ReportFactory, ReportLoading } from '@ltvco/refresh-lib/v1';
import {
  FraudEmailOverview,
  ReportGeneratingScreen,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  ReportActionsWithDateUpdated,
  ReportRating,
  AdditionalDetailsSection,
  PotentialOwnerCta as PotentialOwnerCtaV2,
  ReportNullState,
} from '@ltvco/refresh-lib/v2';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ReportRouteProps } from 'components/reports/types';
import { getFraudEmailNavLinkDataV2 } from 'navLinkData/fraudNavLinkData';
// END IMPORTS

export function EmailFraudReportPage({
  permalink,
  firstReportScreen,
}: ReportRouteProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const theme = useTheme();
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);
  const [, triggerUpdate] = useState(0);
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const reportType = 'email-fraud';
  const zeroed = isZeroed(queryResult);
  // END DERIVED_DATA_AND_UTILS

  // START EARLY_RETURNS
  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="email-fraud"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const report = ReportFactory.create(queryResult?.data, 'email-fraud');

  const queryParams = new URLSearchParams(search);
  const fallbackEmail = queryParams.get('email') || '';
  const lowerCaseEmailAddress =
    report.data.address?.toLowerCase() ?? fallbackEmail;
  document.title = `${lowerCaseEmailAddress} - BeenVerified`;
  const fraudEmailNavLinkDataV2 = getFraudEmailNavLinkDataV2();
  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  if (!report) {
    return <ReportLoading menuItems={2} />;
  }

  if (report?.data?.isEmpty) {
    return <ReportNullState />;
  }

  const {
    isCatchAll,
    isFrequentComplainer,
    activity,
    isLeaked,
    validityScore,
    isRecentAbuse,
    domain,
  } = report.data;

  const { isRiskyTld, isDmarcRecord, isDnsValid, mxRecords, suggestedDomain } =
    domain || {};
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Email Fraud"
            headerTitle={lowerCaseEmailAddress}
            menuItems={
              fraudEmailNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        <Box marginTop={0} width="100%">
          <Box
            sx={{
              height: 30,
              backgroundColor: theme.palette.background.default,
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Fraud Scan"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />

          <FraudEmailOverview reportData={report.data} />

          <Stack spacing={2}>
            <PotentialOwnerCtaV2
              email={lowerCaseEmailAddress}
              reportType="email"
            />
            <AdditionalDetailsSection
              isRecentAbuse={isRecentAbuse}
              isDmarcRecord={isDmarcRecord}
              typo={suggestedDomain}
              isCatchAll={isCatchAll}
              isFrequentComplainer={isFrequentComplainer}
              isLeaked={isLeaked}
              isRiskyTld={isRiskyTld}
              isDnsValid={isDnsValid}
              validityScore={validityScore}
              userActivity={activity}
              mxRecords={mxRecords}
            />
            <ReportRating
              rating={report.data.meta?.rating || null}
              report_type={reportType}
            />
          </Stack>
        </Box>
      </Stack>
      <ReportNavigationMobileMenu
        reportType="Fraud Scan"
        headerTitle={lowerCaseEmailAddress}
        menuItems={
          fraudEmailNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
      />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
  // END RENDER
}
